import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { IconButton, InputAdornment, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import SearchIcon from 'assets/icons/icn _ 16px _ Search.svg';
import { useStyles as useDialogStyles } from 'components/dialogs/styles';
import FilteringNoResults from 'components/emptyStates/FilteringNoResults';
import TMXInput from 'components/inputs/TMXInput';
import StandardTable from 'components/tables/dndStandardTable/StandardTable';
import { type TableConfigInterface } from 'components/tables/dndVirtualizedTable/typedefs';
import { BodyLarge } from 'components/typography/Typography';
import { type SeedAssignment } from 'typedefs/store/tmxTypes';
import { participantName } from 'utils/participant';

import { useStyles } from './style';
import { useColumns } from './utils';

interface AssignParticipantTableProps {
  players: any[];
  seedAssignments?: SeedAssignment[];
  selectedRow: string;
  isModifyDoublesPair?: boolean;
  isTeam?: boolean;
  isDoubles?: boolean;
  onChangeSelected: (participantId: string) => void;
}

const AssignParticipantTable: React.FC<React.PropsWithChildren<AssignParticipantTableProps>> = ({
  players,
  seedAssignments,
  selectedRow,
  isModifyDoublesPair,
  isTeam = false,
  isDoubles,
  onChangeSelected,
}) => {
  const { classes } = useStyles();
  const { classes: dialogClasses } = useDialogStyles();
  const { t } = useTranslation();
  const playersWithIds = useMemo(
    () =>
      players?.map((p, index) => {
        return { ...p, id: p.participant?.participantId, index: index + 1 };
      }),
    [players],
  );

  const handleRowClick = useCallback(
    (participantId: string) => {
      onChangeSelected(participantId);
    },
    [onChangeSelected],
  );
  const columnParams = useMemo(
    () => ({
      selectedRow,
      onChangeSelected,
      isTeam,
      isDoubles,
      isModifyDoublesPair,
      seedAssignments,
    }),
    [selectedRow, onChangeSelected, isTeam, isDoubles, isModifyDoublesPair, seedAssignments],
  );

  const columns = useColumns(columnParams);

  const [filterParticipantsValue, setFilterParticipantsValue] = useState<string>('');
  const filteredParticipants = useMemo(
    () =>
      playersWithIds.filter((participant) =>
        participantName(participant?.participant).toLowerCase().includes(filterParticipantsValue?.toLowerCase()),
      ),
    [filterParticipantsValue, playersWithIds],
  );

  const selectedParticipantName = useMemo(
    () => filteredParticipants?.find((p) => p?.id === selectedRow)?.participant?.participantName,
    [filteredParticipants, selectedRow],
  );

  const handleOnChangeFilterParticipants = useCallback(
    (event) => {
      setFilterParticipantsValue(event.target.value);
      const selectedParticipantId = filteredParticipants
        ?.find((p) => selectedRow === p?.id)
        ?.participant?.participantName?.toLowerCase()
        ?.includes(event.target.value?.toLowerCase())
        ? selectedRow
        : '';
      onChangeSelected(selectedParticipantId);
    },
    [filteredParticipants, onChangeSelected, selectedRow],
  );

  const tableConfig: TableConfigInterface = useMemo(
    () => ({
      className: classes.RTableConfig,
      tableHeight: 400,
    }),
    [classes],
  );

  return (
    <Grid item>
      <Grid container className={classes.tableContainer} direction="column">
        <Grid item>
          <TMXInput
            autoFocus
            fullWidth
            placeholder={isTeam ? t('searchTeams') : t('searchPlayers')}
            startAdornment={
              <InputAdornment position="end">
                <Tooltip aria-label={t('search')} title={t('search')}>
                  <IconButton size="large">
                    <img alt="search-icon" src={SearchIcon} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            }
            onChange={handleOnChangeFilterParticipants}
          />
        </Grid>
        <Grid item>
          {filteredParticipants?.length ? (
            <StandardTable
              clickable
              columns={columns}
              data={filteredParticipants}
              tableConfig={tableConfig}
              onRowClick={(_, rowItem) => handleRowClick(rowItem?.participant?.participantId)}
            />
          ) : (
            <FilteringNoResults />
          )}
        </Grid>
      </Grid>
      {selectedParticipantName && (
        <Grid item xs={12}>
          <BodyLarge>
            <>
              {isTeam ? t('selectedTeam') : t('selectedPlayer')}:{' '}
              <span className={dialogClasses.selectedPlayer}>{selectedParticipantName}</span>
            </>
          </BodyLarge>
        </Grid>
      )}
    </Grid>
  );
};

export default AssignParticipantTable;
